import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PaymentGateway, PaymentGatewayRequest } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";

export default function EditGateway() {
  const navigate = useNavigate();
  const { id } = useParams();
  const gatewayId = Number(id);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    key: "",
    is_active: true,
    type: "usdt" as "usdt" | "irr",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setMessage } = useContext(AppContext);

  const isFormValid = useMemo(() => {
    return Boolean(form.name.trim() && form.slug.trim() && form.key.trim() && form.type);
  }, [form]);

  useEffect(() => {
    if (!gatewayId) {
      setMessage({ type: "error", message: "شناسه درگاه معتبر نیست." });
      navigate("/admin/gateways");
      return;
    }

    const fetchGateway = async () => {
      setIsLoading(true);
      try {
        const response = await appRepo.getGateway(gatewayId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت اطلاعات درگاه ناموفق بود." });
          navigate("/admin/gateways");
          return;
        }

        const data: PaymentGateway = response.data;
        setForm({
          name: data.name ?? "",
          slug: data.slug ?? "",
          key: data.key ?? "",
          is_active: Boolean(data.is_active),
          type: data.type === "irr" ? "irr" : "usdt",
        });
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/gateways");
      } finally {
        setIsLoading(false);
      }
    };

    fetchGateway();
  }, [gatewayId, navigate, setMessage]);

  const onChangeField = (field: "name" | "slug" | "key" | "type", value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();

    if (!isFormValid || !gatewayId) {
      setMessage({ type: "error", message: "لطفا همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: PaymentGatewayRequest = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        key: form.key.trim(),
        is_active: form.is_active,
        type: form.type,
      };

      const response = await appRepo.updateGateway(gatewayId, payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ویرایش درگاه با خطا مواجه شد." });
        return;
      }

      setMessage({ type: "success", message: "درگاه با موفقیت ویرایش شد." });
      navigate("/admin/gateways");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. لطفا دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="ویرایش درگاه" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت اطلاعات درگاه...
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ویرایش درگاه" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm text-slate-300">نام درگاه</span>
            <input
              value={form.name}
              onChange={(event) => onChangeField("name", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="مثال: زرین پال"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">اسلاگ</span>
            <input
              value={form.slug}
              onChange={(event) => onChangeField("slug", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="zarinpal"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">کلید درگاه</span>
            <input
              value={form.key}
              onChange={(event) => onChangeField("key", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
              placeholder="gateway-key"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">نوع درگاه</span>
            <select
              value={form.type}
              onChange={(event) => onChangeField("type", event.target.value as "usdt" | "irr")}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            >
              <option value="usdt">ارزی</option>
              <option value="irr">ریال</option>
            </select>
          </label>

          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
              className="h-4 w-4 accent-indigo-500"
            />
            <span className="text-sm text-slate-300">درگاه فعال باشد</span>
          </label>

          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
            <Link
              to="/admin/gateways"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
